import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot='dialog' {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot='dialog-overlay'
      className={cn(
        'hc:data-[state=open]:animate-in hc:data-[state=closed]:animate-out hc:data-[state=closed]:fade-out-0 hc:data-[state=open]:fade-in-0 hc:fixed hc:inset-0 hc:z-50 hc:bg-black/50',
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot='dialog-portal'>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot='dialog-content'
        className={cn(
          'hc:bg-background hc:data-[state=open]:animate-in hc:data-[state=closed]:animate-out hc:data-[state=closed]:fade-out-0 hc:data-[state=open]:fade-in-0 hc:data-[state=closed]:zoom-out-95 hc:data-[state=open]:zoom-in-95 hc:fixed hc:top-[50%] hc:left-[50%] hc:z-50 hc:grid hc:w-full hc:max-w-[calc(100%-2rem)] hc:translate-x-[-50%] hc:translate-y-[-50%] hc:gap-4 hc:rounded-lg hc:border hc:p-6 hc:shadow-lg hc:duration-200 hc:sm:max-w-lg',
          className
        )}
        {...props}>
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot='dialog-close'
            className="hc:ring-offset-background hc:focus:ring-ring hc:data-[state=open]:bg-accent hc:data-[state=open]:text-muted-foreground hc:absolute hc:top-4 hc:right-4 hc:rounded-xs hc:opacity-70 hc:transition-opacity hc:hover:opacity-100 hc:focus:ring-2 hc:focus:ring-offset-2 hc:focus:outline-hidden hc:disabled:pointer-events-none hc:[&_svg]:pointer-events-none hc:[&_svg]:shrink-0 hc:[&_svg:not([class*='size-'])]:size-4">
            <XIcon />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-header'
      className={cn('hc:flex hc:flex-col hc:gap-2 hc:text-center hc:sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'hc:flex hc:flex-col-reverse hc:gap-2 hc:sm:flex-row hc:sm:justify-end',
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn('hc:text-muted-foreground hc:text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
